import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { kcFetch, API_BASE } from "../../lib/apiClient";
import { useAppSettings, AppLogo } from "../../lib/AppSettingsContext";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// PublicEventRegisterPage — accessed via the per-event QR code
// (printed via Admin > QR Poster > Event QR card).
//   • No login required
//   • Loads minimal event info from /api/public/events/{id}
//   • Collects Full Name, Phone, Email
//   • POSTs /api/public/events/{id}/register
// ============================================================
export default function PublicEventRegisterPage() {
  const { eventId } = useParams();
  const { settings } = useAppSettings();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await kcFetch(`${API_BASE}/public/events/${eventId}`);
        if (!r.ok) throw new Error("Event not found");
        const j = await r.json();
        if (alive) setEvent(j);
      } catch (e) {
        if (alive) setError(e.message || "Event not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [eventId]);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) { setError("Please enter your full name."); return; }
    if (!form.phone.trim() && !form.email.trim()) { setError("Please share a phone or email."); return; }
    setSubmitting(true);
    try {
      const r = await kcFetch(`${API_BASE}/public/events/${eventId}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not register. Please try again.");
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "Could not register.");
    } finally {
      setSubmitting(false);
    }
  };

  const appName = settings.appName || "Kingdom Connect";
  const tagline = settings.appTagline || "To Communicate the Love of Christ";

  return (
    <div className="public-book-page" data-testid="public-event-register-page">
      <style>{CSS}</style>
      <div className="public-book-topbar">
        <AppLogo size={56} className="public-book-logo" style={{ borderRadius: 10 }} />
        <div className="public-book-brand">
          <div className="pbb-title">{appName}</div>
          <div className="pbb-sub">{tagline}</div>
        </div>
      </div>
      <div className="public-book-wrap">
        {loading ? (
          <div className="public-book-empty">Loading event…</div>
        ) : !event ? (
          <div className="public-book-error" data-testid="event-not-found">{error || "Event not found."}</div>
        ) : done ? (
          <div className="public-success-card" data-testid="event-register-success">
            <div className="public-success-ring" aria-hidden="true"><CustomIcon name="check" size={40} /></div>
            <h1>You&apos;re In!</h1>
            <p>Thank you <strong>{form.name}</strong>. We&apos;ve registered you for <strong>{event.name}</strong>{event.date ? ` on ${event.date}` : ""}{event.time ? ` at ${event.time}` : ""}. See you then!</p>
            <a href="/" className="public-success-btn">Return Home</a>
          </div>
        ) : (
          <>
            <header className="public-book-hero">
              <h1>Register for {event.name}</h1>
              {(event.date || event.time) && (
                <p>{event.date}{event.time ? ` · ${event.time}` : ""}</p>
              )}
              {event.description && <p>{event.description}</p>}
            </header>
            <section className="public-book-section">
              <h2>Your Details</h2>
              <form className="public-book-form" noValidate onSubmit={submit} data-testid="event-register-form">
                <label className="public-book-field">
                  <span className="pbf-label">Full Name *</span>
                  <input data-testid="er-name" value={form.name} onChange={update("name")} placeholder="Your full name" autoComplete="name" />
                </label>
                <div className="public-book-row">
                  <label className="public-book-field">
                    <span className="pbf-label">Phone Number</span>
                    <input data-testid="er-phone" value={form.phone} onChange={update("phone")} placeholder="+233 ..." autoComplete="tel" />
                  </label>
                  <label className="public-book-field">
                    <span className="pbf-label">Email</span>
                    <input data-testid="er-email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" autoComplete="email" />
                  </label>
                </div>
                {error && <div className="public-book-error" data-testid="event-register-error">{error}</div>}
                <button type="submit" disabled={submitting} className="public-book-submit" data-testid="event-register-submit">
                  {submitting ? "Submitting…" : "Register"}
                </button>
              </form>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

// Re-use the same CSS shape as the booking page; minimal duplication for the
// public bundle to stay self-contained.
const CSS = `
  .public-book-page { min-height: 100vh; background: var(--kc-surface); color: var(--kc-text); font-family: 'Montserrat', sans-serif; padding: 24px 16px 80px; }
  .public-book-topbar { max-width: 920px; margin: 0 auto 24px; display: flex; align-items: center; gap: 14px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); padding-bottom: 16px; }
  .public-book-logo { width: 56px; height: auto; border-radius: 10px; }
  .public-book-brand .pbb-title { font-family: 'Cinzel', serif; font-size: 18px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--kc-text); }
  .public-book-brand .pbb-sub { font-size: 11px; color: var(--kc-accent); font-style: italic; letter-spacing: 0.08em; text-transform: uppercase; margin-top: 4px; }
  .public-book-wrap { max-width: 920px; margin: 0 auto; display: flex; flex-direction: column; gap: 28px; }
  .public-book-hero h1 { font-family: 'Cinzel', serif; font-size: 28px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-text); margin: 0 0 8px; }
  .public-book-hero p { color: var(--kc-sub); font-size: 14px; line-height: 1.55; margin: 0 0 4px; max-width: 640px; }
  .public-book-section { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); border-radius: 16px; padding: 22px; }
  .public-book-section h2 { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-accent); margin: 0 0 16px; }
  .public-book-form { display: flex; flex-direction: column; gap: 14px; }
  .public-book-row { display: grid; gap: 14px; grid-template-columns: 1fr; }
  @media (min-width: 640px) { .public-book-row { grid-template-columns: 1fr 1fr; } }
  .public-book-field { display: flex; flex-direction: column; gap: 6px; }
  .pbf-label { font-size: 11px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-sub); }
  .public-book-form input { background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text); border-radius: 10px; padding: 11px 12px; font-family: 'Montserrat', sans-serif; font-size: 14px; outline: none; }
  .public-book-form input:focus { border-color: var(--kc-accent); }
  .public-book-submit { background: var(--kc-accent); color: #1A1A1A; border: none; border-radius: 12px; padding: 14px 18px; cursor: pointer; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; font-size: 13px; margin-top: 8px; }
  .public-book-submit:disabled { opacity: 0.6; cursor: not-allowed; }
  .public-book-error { background: rgba(255,107,107,0.10); border: 1px solid rgba(255,107,107,0.45); color: #ff9b9b; border-radius: 10px; padding: 10px 12px; font-size: 13px; }
  .public-book-empty { color: var(--kc-sub); font-size: 13px; padding: 16px; text-align: center; }
  .public-success-card { max-width: 540px; margin: 56px auto; background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 16px; padding: 36px 28px; text-align: center; }
  .public-success-ring { width: 72px; height: 72px; border-radius: 50%; background: color-mix(in srgb, var(--kc-accent) 12%, transparent); border: 2px solid var(--kc-accent); color: var(--kc-accent); font-size: 34px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 18px; }
  .public-success-card h1 { font-family: 'Cinzel', serif; font-size: 24px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-text); margin: 0 0 10px; }
  .public-success-card p { color: var(--kc-sub); font-size: 14px; line-height: 1.6; margin: 0 0 20px; }
  .public-success-btn { display: inline-block; background: transparent; border: 1.5px solid var(--kc-accent); color: var(--kc-accent); border-radius: 10px; padding: 10px 18px; text-decoration: none; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; font-size: 12px; }
`;
